import React, { useState } from 'react';
import { Send, CheckCircle2, AlertCircle, Loader2, User, Phone, Mail, MessageSquare, MapPin } from 'lucide-react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    postalCode: '',
    description: '',
    website_url: '', // Honeypot field
  });

  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          Navn: formData.name,
          Telefonnummer: formData.phone,
          'E-mail': formData.email,
          Postnummer: formData.postalCode,
          Beskrivelse: formData.description,
          website_url: formData.website_url, // Honeypot
        }),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          postalCode: '',
          description: '',
          website_url: '',
        });
      } else {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.message || 'Der kunne ikke oprettes forbindelse til serveren.');
    }
  };

  if (status === 'success') {
    return (
      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-3xl p-8 text-center space-y-4 max-w-xl mx-auto">
        <div className="inline-flex p-3 rounded-full bg-emerald-500/20 text-emerald-400">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <h3 className="text-xl font-bold text-white">Tak for din henvendelse!</h3>
        <p className="text-sm text-slate-300 leading-relaxed">
          Vi har modtaget din besked og vil vende tilbage til dig så hurtigt som muligt. Vi glæder os til at hjælpe dig med dit malerprojekt.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-[#0b1024] border border-slate-800/80 rounded-3xl p-6 sm:p-8 shadow-xl space-y-6 w-full max-w-xl mx-auto relative overflow-hidden">
      {/* Decorative top bar */}
      <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#F1B528] to-[#D99C1B]" />

      <div className="space-y-2">
        <h3 className="text-xl font-bold text-white tracking-tight">Skriv til os</h3>
        <p className="text-xs text-slate-400">Udfyld formularen nedenfor, så vender vi tilbage hurtigst muligt.</p>
      </div>

      {/* Honeypot Field - Absolutely hidden from humans */}
      <div className="sr-only absolute -top-[9999px] -left-[9999px]" aria-hidden="true">
        <label htmlFor="website_url">Website URL</label>
        <input
          type="text"
          id="website_url"
          name="website_url"
          value={formData.website_url}
          onChange={handleChange}
          tabIndex="-1"
          autoComplete="off"
        />
      </div>

      {/* Form Fields */}
      <div className="space-y-5">
        {/* Name Field */}
        <div className="space-y-1.5">
          <label htmlFor="name" className="block text-[10px] font-bold text-slate-300 uppercase tracking-wider">
            Navn <span className="text-[#F1B528]">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500">
              <User className="w-4 h-4" />
            </div>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              autoComplete="name"
              className="w-full pl-11 pr-4 py-3.5 rounded-full border border-slate-800 focus:border-[#F1B528] focus:ring-2 focus:ring-[#F1B528]/10 outline-none transition-all text-xs text-white bg-slate-900/60 placeholder-slate-500"
              placeholder="Dit fulde navn"
            />
          </div>
        </div>

        {/* Contact Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {/* Phone Field */}
          <div className="space-y-1.5">
            <label htmlFor="phone" className="block text-[10px] font-bold text-slate-300 uppercase tracking-wider">
              Telefonnummer <span className="text-[#F1B528]">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500">
                <Phone className="w-4 h-4" />
              </div>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                autoComplete="tel"
                inputMode="tel"
                className="w-full pl-11 pr-4 py-3.5 rounded-full border border-slate-800 focus:border-[#F1B528] focus:ring-2 focus:ring-[#F1B528]/10 outline-none transition-all text-xs text-white bg-slate-900/60 placeholder-slate-500"
                placeholder="Telefonnummer"
              />
            </div>
          </div>

          {/* Email Field */}
          <div className="space-y-1.5">
            <label htmlFor="email" className="block text-[10px] font-bold text-slate-300 uppercase tracking-wider">
              E-mail <span className="text-[#F1B528]">*</span>
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500">
                <Mail className="w-4 h-4" />
              </div>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                autoComplete="email"
                inputMode="email"
                className="w-full pl-11 pr-4 py-3.5 rounded-full border border-slate-800 focus:border-[#F1B528] focus:ring-2 focus:ring-[#F1B528]/10 outline-none transition-all text-xs text-white bg-slate-900/60 placeholder-slate-500"
                placeholder="E-mailadresse"
              />
            </div>
          </div>
        </div>

        {/* Postal Code Field */}
        <div className="space-y-1.5">
          <label htmlFor="postalCode" className="block text-[10px] font-bold text-slate-300 uppercase tracking-wider">
            Postnummer <span className="text-[#F1B528]">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500">
              <MapPin className="w-4 h-4" />
            </div>
            <input
              type="text"
              id="postalCode"
              name="postalCode"
              required
              value={formData.postalCode}
              onChange={handleChange}
              autoComplete="postal-code"
              inputMode="numeric"
              pattern="[0-9]*"
              className="w-full pl-11 pr-4 py-3.5 rounded-full border border-slate-800 focus:border-[#F1B528] focus:ring-2 focus:ring-[#F1B528]/10 outline-none transition-all text-xs text-white bg-slate-900/60 placeholder-slate-500"
              placeholder="Indtast postnummer (f.eks. 7100)"
            />
          </div>
        </div>

        {/* Description Field */}
        <div className="space-y-1.5">
          <label htmlFor="description" className="block text-[10px] font-bold text-slate-300 uppercase tracking-wider">
            Beskrivelse <span className="text-slate-500">(valgfri)</span>
          </label>
          <div className="relative">
            <div className="absolute top-3.5 left-0 pl-4 pointer-events-none text-slate-500">
              <MessageSquare className="w-4 h-4" />
            </div>
            <textarea
              id="description"
              name="description"
              rows="4"
              value={formData.description}
              onChange={handleChange}
              className="w-full pl-11 pr-4 py-3.5 rounded-3xl border border-slate-800 focus:border-[#F1B528] focus:ring-2 focus:ring-[#F1B528]/10 outline-none transition-all text-xs text-white bg-slate-900/60 placeholder-slate-500 resize-none"
              placeholder="Beskriv dit malerprojekt her..."
            />
          </div>
        </div>
      </div>

      {/* Error Message */}
      {status === 'error' && (
        <div className="flex items-start gap-2.5 p-4 rounded-2xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
          <AlertCircle className="w-4 h-4 shrink-0 text-red-400 mt-0.5" />
          <span>{errorMessage}</span>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={status === 'submitting'}
        className="flex items-center justify-center gap-2 w-full bg-[#F1B528] hover:bg-[#D99C1B] text-slate-950 py-4 rounded-full font-bold transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#F1B528]/10 cursor-pointer text-sm"
      >
        {status === 'submitting' ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Sender besked...
          </>
        ) : (
          <>
            Send besked
            <Send className="w-4 h-4" />
          </>
        )}
      </button>
    </form>
  );
}
